import { NarrationRealtimeTransportError } from './narrationRealtimeTransport';

export type VoiceConnectionStage = 'browser' | 'session' | 'microphone' | 'capture' | 'offer' | 'signaling' | 'answer' | 'connection';

export type VoiceConnectionFailure = {
  stage: VoiceConnectionStage;
  retryable: boolean;
  message: string;
  microphoneDenied: boolean;
  recoveryHint: string;
  status: number | null;
};

const stageLabels: Record<VoiceConnectionStage, string> = {
  browser: 'Voice browser support',
  session: 'Voice session setup',
  microphone: 'Microphone setup',
  capture: 'Voice audio capture setup',
  offer: 'Voice connection setup',
  signaling: 'Voice connection request',
  answer: 'Voice connection response',
  connection: 'Voice connection',
};

export class VoiceConnectionHttpError extends Error {
  constructor(public readonly status: number) {
    super(`Connection request failed (HTTP ${status}).`);
    this.name = 'VoiceConnectionHttpError';
  }
}

// Keep local browser exceptions useful without retaining URLs, credentials,
// connection descriptions, or provider response bodies in the UI/diagnostics.
const safeBrowserMessage = (message: string): string => {
  const comparable = message.slice(0, 4096)
    .replace(/\\u([a-f\d]{4})/gi, (_, code: string) => String.fromCharCode(parseInt(code, 16)))
    .replace(/\\x([a-f\d]{2})|%([a-f\d]{2})/gi, (_, escaped: string | undefined, encoded: string | undefined) => String.fromCharCode(parseInt(escaped ?? encoded ?? '', 16)));
  if (/\bsdp\b|\bv=0\b|(?:^|[\s"'\\])(?:a=\S|m=audio|o=-?\d)/i.test(comparable)) return 'The connection description could not be applied.';
  if (/client[_-]?secret|api[_-]?key|authorization|password|\b(?:token|secret)\b|\bBearer\s|\b(?:sk|ek)[-_][A-Za-z0-9_-]+|\beyJ[A-Za-z0-9_-]+\./i.test(comparable)) return 'The browser could not complete this setup step.';
  return message
    .replace(/https?:\/\/[^\s"'<>]+/gi, '[resource]')
    .replace(/\bBearer\s+\S+/gi, 'Bearer [redacted]')
    .replace(/\b(?:sk|ek)[-_][A-Za-z0-9_-]+\b/g, '[redacted]')
    .replace(/\beyJ[A-Za-z0-9_-]+(?:\.[A-Za-z0-9_-]+){1,2}\b/g, '[redacted]')
    .replace(/\b(?:client_secret|api_key|authorization|password|token|secret)\s*[:=]\s*[^\s,;]+/gi, '[redacted]')
    .replace(/\b[A-Za-z0-9_-]{40,}\b/g, '[redacted]')
    .replace(/\s+/g, ' ').trim().slice(0, 240);
};

export const describeVoiceConnectionFailure = (failure: unknown, stage: VoiceConnectionStage): VoiceConnectionFailure => {
  const error = failure as { name?: unknown; message?: unknown; code?: unknown; response?: { status?: unknown } } | null;
  const name = typeof error?.name === 'string' ? error.name : '';
  const status = failure instanceof VoiceConnectionHttpError ? failure.status
    : typeof error?.response?.status === 'number' ? error.response.status : null;
  const permissionDenied = name === 'NotAllowedError' || name === 'PermissionDeniedError';
  const microphoneDenied = stage === 'microphone' && permissionDenied;
  const permanentNames = ['NotFoundError', 'DevicesNotFoundError', 'NotReadableError', 'TrackStartError',
    'OverconstrainedError', 'ConstraintNotSatisfiedError', 'SecurityError', 'NotSupportedError',
    'InvalidStateError', 'SyntaxError', 'OperationError'];
  const rawMessage = failure instanceof Error || (typeof DOMException !== 'undefined' && failure instanceof DOMException)
    ? failure.message : '';
  const retryable = failure instanceof NarrationRealtimeTransportError ? failure.retryable
    : !permissionDenied && !permanentNames.includes(name) && (status !== null
    ? [408, 425, 429].includes(status) || status >= 500
    : name === 'NetworkError' || name === 'TimeoutError' || error?.code === 'ERR_NETWORK'
      || error?.code === 'ECONNABORTED' || error?.code === 'ETIMEDOUT'
      || ((stage === 'signaling' || stage === 'session') && name === 'TypeError'
        && /fetch|network|load failed/i.test(rawMessage)) || stage === 'connection');
  const detail = status !== null ? `Connection request failed (HTTP ${status}).`
    : safeBrowserMessage(rawMessage) || 'This step could not be completed.';
  const exceptionName = name && !['Error', 'VoiceConnectionHttpError', 'NarrationRealtimeTransportError'].includes(name) && /^[A-Za-z][A-Za-z0-9]{0,63}$/.test(name)
    ? `${name}: ` : '';
  const recoveryHint = microphoneDenied ? 'Allow microphone access in your browser, then reconnect.'
    : stage === 'microphone' ? ['NotFoundError', 'DevicesNotFoundError'].includes(name)
      ? 'Choose a connected microphone in your browser, then reconnect.'
      : 'Close other apps using the microphone or choose another input, then reconnect.'
    : stage === 'browser' ? 'Use a browser with microphone support, then reconnect.'
    : stage === 'capture' || stage === 'answer' || stage === 'offer' ? 'Refresh this page, then reconnect.'
    : status === 401 || status === 403 ? 'Refresh this page and check that you are signed in, then reconnect.'
    : retryable ? 'Check your connection, then reconnect if needed.' : 'Refresh this page, then reconnect.';
  return { stage, retryable, microphoneDenied, recoveryHint, status,
    message: `${stageLabels[stage]}: ${exceptionName}${detail} ${recoveryHint}` };
};
